import { isNotNullable } from '@spendesk/general-type-helpers/misc';

import { type TGlobalFunctionTyped } from 'src/core/common/hooks/useTranslation';
import { TypeGuardError } from 'src/core/utils/switchGuard';

import {
  type ApprovalRuleExport,
  type ApprovalRangeExport,
  type ApprovalSchemeExport,
  type ApprovalRuleTypeExport,
} from './approvalWorkflowExportTypes';

const formatRange = (
  range: ApprovalRangeExport,
  t: TGlobalFunctionTyped,
): string | undefined => {
  switch (range.type) {
    case 'noRange':
      return undefined;
    case 'upTo':
      return t('approvalWorkflows.export.amountUpTo', {
        upToAmount: range.upTo,
      });
    case 'from':
      return t('approvalWorkflows.export.amountFrom', {
        fromAmount: range.from,
      });
    case 'fromUpTo':
      return t('approvalWorkflows.export.amountFromUpTo', {
        fromAmount: range.from,
        upToAmount: range.upTo,
      });
    default:
      throw new TypeGuardError(range, 'Unknown rule range type');
  }
};

const formatRuleType = (
  type: ApprovalRuleTypeExport,
  t: TGlobalFunctionTyped,
): string => {
  switch (type) {
    case 'anyOf':
      return t('approvalWorkflows.export.anyApproverOf');
    case 'allInOrder':
      return t('approvalWorkflows.export.allApproverInOrder');
    default:
      throw new TypeGuardError(type, 'Unknown rule type');
  }
};

const formatApprovers = (rule: ApprovalRuleExport): string => {
  switch (rule.type) {
    case 'anyOf':
      return rule.approvers.map((approver) => `- ${approver.name}`).join('\n');
    case 'allInOrder':
      return rule.approvers
        .map((approver, index) => `${index + 1}. ${approver.name}`)
        .join('\n');
    default:
      throw new TypeGuardError(rule.type, 'Unknown rule type');
  }
};

const formatRule = (
  rule: ApprovalRuleExport,
  t: TGlobalFunctionTyped,
): string =>
  [
    formatRange(rule.range, t),
    formatRuleType(rule.type, t),
    formatApprovers(rule),
  ]
    .filter(isNotNullable)
    .join('\n');

export const formatApprovalScheme = (
  scheme: ApprovalSchemeExport,
  t: TGlobalFunctionTyped,
): string => {
  return scheme.rules.map((rule) => formatRule(rule, t)).join('\n\n');
};
